<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    import { SvelteMap } from 'svelte/reactivity';
    import { __, focusTrap } from '@oat-sa-private/ui-core';
    import { IconBarButton, Slider } from '@oat-sa-private/ui-elements';

    /**
     * @typedef {Object} Props
     * @property {String} src
     * @property {String} [alt]
     * @property {Function} [onClose]
     */
    /** @type {Props} */
    let { src, alt = '', onClose = () => {} } = $props();

    const pointers = new SvelteMap();
    const minimumScale = 1;
    const maximumScale = 5;
    const zoomStep = 0.5;
    const wheelZoomSensitivity = 0.001;
    const zoomPercentFactor = 100;
    const panClickThreshold = 4;
    const zoomPercentMinimum = minimumScale * zoomPercentFactor;
    const zoomPercentMaximum = maximumScale * zoomPercentFactor;
    const zoomPercentStep = zoomStep * zoomPercentFactor;

    let dialog = $state();
    let viewport = $state();
    let image = $state();
    let transformState = $state({
        scale: minimumScale,
        x: 0,
        y: 0
    });
    let bounds = $state({
        imageWidth: 0,
        imageHeight: 0,
        viewportWidth: 0,
        viewportHeight: 0
    });
    let gesture = $state(null);
    let ignoreNextClick = $state(false);

    const imagePreviewLabel = __('Image preview');
    let dialogLabel = $derived(alt ? `${imagePreviewLabel}: ${alt}` : imagePreviewLabel);
    let imageStyle = $derived(
        `transform: translate3d(${transformState.x}px, ${transformState.y}px, 0) scale(${transformState.scale});`
    );
    let zoomPercent = $derived(Math.round(transformState.scale * zoomPercentFactor));
    let isAtMinimumScale = $derived(transformState.scale <= minimumScale);
    let isAtMaximumScale = $derived(transformState.scale >= maximumScale);

    const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

    function getPanLimits(scale) {
        return {
            x: Math.max(0, (bounds.imageWidth * scale - bounds.viewportWidth) / 2),
            y: Math.max(0, (bounds.imageHeight * scale - bounds.viewportHeight) / 2)
        };
    }

    function updateState(nextState) {
        const scale = clamp(nextState.scale, minimumScale, maximumScale);
        const limits = getPanLimits(scale);

        transformState = {
            scale,
            x: scale === minimumScale ? 0 : clamp(nextState.x, -limits.x, limits.x),
            y: scale === minimumScale ? 0 : clamp(nextState.y, -limits.y, limits.y)
        };
    }

    function setScale(scale) {
        updateState({ ...transformState, scale });
    }

    function zoomBy(delta) {
        setScale(transformState.scale + delta);
    }

    function panBy(deltaX, deltaY) {
        updateState({
            ...transformState,
            x: transformState.x + deltaX,
            y: transformState.y + deltaY
        });
    }

    function reset() {
        transformState = {
            scale: minimumScale,
            x: 0,
            y: 0
        };
    }

    function updateBounds() {
        if (!image || !viewport) {
            return;
        }
        const viewportRect = viewport.getBoundingClientRect();
        const imageRect = image.getBoundingClientRect();
        bounds = {
            imageWidth: image.offsetWidth || imageRect.width / transformState.scale,
            imageHeight: image.offsetHeight || imageRect.height / transformState.scale,
            viewportWidth: viewportRect.width,
            viewportHeight: viewportRect.height
        };
        updateState(transformState);
    }

    function close() {
        onClose();
    }

    function handleKeyDown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
        }
    }

    function handleZoomSliderChange(event) {
        setScale(event.detail.value / zoomPercentFactor);
    }

    function handleOverlayClick(event) {
        if (event.target.closest('.image-lightbox-controls')) {
            return;
        }
        if (ignoreNextClick) {
            ignoreNextClick = false;
            return;
        }
        close();
    }

    function getPointerDistance() {
        const [first, second] = [...pointers.values()];
        return first && second ? Math.hypot(second.x - first.x, second.y - first.y) : 0;
    }

    function handlePointerDown(event) {
        event.preventDefault();
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        event.currentTarget.setPointerCapture?.(event.pointerId);

        if (pointers.size === 1) {
            gesture = {
                type: 'single',
                x: event.clientX,
                y: event.clientY,
                lastX: event.clientX,
                lastY: event.clientY,
                moved: false
            };
        } else if (pointers.size === 2) {
            gesture = {
                type: 'pinch',
                distance: getPointerDistance(),
                scale: transformState.scale
            };
        }
    }

    function handlePointerMove(event) {
        const currentPointer = pointers.get(event.pointerId);
        if (!currentPointer) {
            return;
        }

        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        event.preventDefault();

        if (pointers.size === 2 && gesture?.type === 'pinch') {
            const distance = getPointerDistance();
            if (gesture.distance > 0) {
                setScale(gesture.scale * (distance / gesture.distance));
                ignoreNextClick = true;
            }
            return;
        }

        if (pointers.size === 1 && gesture?.type === 'single') {
            const deltaX = event.clientX - gesture.lastX;
            const deltaY = event.clientY - gesture.lastY;
            const totalDeltaX = event.clientX - gesture.x;
            const totalDeltaY = event.clientY - gesture.y;

            if (Math.hypot(totalDeltaX, totalDeltaY) >= panClickThreshold) {
                gesture.moved = true;
                ignoreNextClick = true;
            }

            if (!isAtMinimumScale) {
                panBy(deltaX, deltaY);
            }
            gesture.lastX = event.clientX;
            gesture.lastY = event.clientY;
        }
    }

    function handlePointerUp(event) {
        const start = gesture?.type === 'single' ? gesture : null;
        const wasSinglePointer = pointers.size === 1;
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        pointers.delete(event.pointerId);

        if (wasSinglePointer && start?.moved) {
            ignoreNextClick = true;
        }

        if (pointers.size === 0) {
            gesture = null;
        }
    }

    function handleWheel(event) {
        event.preventDefault();
        zoomBy(-event.deltaY * wheelZoomSensitivity);
    }

    $effect(() => {
        if (!dialog) {
            return;
        }

        const previousFocusedElement = document.activeElement;
        dialog?.focus();
        window.addEventListener('resize', updateBounds);

        return () => {
            window.removeEventListener('resize', updateBounds);
            if (previousFocusedElement?.isConnected) {
                previousFocusedElement.focus();
            }
        };
    });
</script>

<style>
    .image-lightbox {
        position: fixed;
        inset: 0;
        z-index: var(--layer-5);
        display: flex;
        flex-direction: column;
        background: rgb(0 0 0 / 88%);
        color: var(--color-text-inverted);
    }

    .image-lightbox-viewport {
        flex: 1 1 auto;
        min-height: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        touch-action: none;
        cursor: grab;
    }

    .image-lightbox-viewport:active {
        cursor: grabbing;
    }

    .image-lightbox-image {
        display: block;
        max-width: calc(100vw - 4rem);
        max-height: calc(100vh - 12rem);
        user-select: none;
        pointer-events: none;
        transform-origin: center;
        transition: transform 80ms linear;
    }

    .image-lightbox-controls {
        position: absolute;
        z-index: 1;
    }

    .image-lightbox-close {
        inset-block-start: 2rem;
        inset-inline-end: 2rem;
        padding: 1rem;
        border-radius: 0.5rem;
        background: var(--color-bg-inverted);
    }

    .image-lightbox-zoom-controls {
        inset-block-end: 3rem;
        inset-inline-start: 50%;
        transform: translateX(-50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1.5rem;
        filter: drop-shadow(0 0 0.4rem var(--color-bg-inverted));
    }

    .image-lightbox-zoom-buttons {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1rem;
    }

    .image-lightbox-zoom-slider {
        box-sizing: border-box;
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: 0;
        inline-size: 20rem;
        block-size: 4rem;
        border: var(--border-thin) solid var(--color-border-inverted);
        border-radius: 0.2rem;
        flex: none;
        order: 1;
        flex-grow: 0;
    }

    .image-lightbox-zoom-slider-button {
        inline-size: 5rem;
        block-size: 3rem;
        padding: 0;
        border: 0;
        background: transparent;
        color: var(--color-text-inverted);
        font-family: var(--font-ui);
        font-size: 4rem;
        font-weight: 300;
        line-height: 1;
        cursor: pointer;
        margin-top: -1.3rem;
    }

    .image-lightbox-zoom-slider :global(.slider) {
        --slider-size: 3.2rem;
        --slider-half-size: 1.6rem;
        --handle-size: 3rem;
        --handle-half-size: 1.5rem;
        --minmax-offset: 0;
        flex: 1 1 auto;
        min-inline-size: 0;
        color: var(--color-text-inverted);
    }

    .image-lightbox-zoom-slider :global(.minmax-labels),
    .image-lightbox-zoom-slider :global(.value-label) {
        display: none;
    }

    .image-lightbox-zoom-slider :global(.track) {
        border-color: var(--color-border-inverted);
    }

    .image-lightbox-zoom-slider :global(.handle) {
        background-color: var(--color-brand);
        border-color: var(--color-brand);
    }

    .image-lightbox-zoom-slider :global(.handle:hover),
    .image-lightbox-zoom-slider :global(.handle:focus) {
        background-color: var(--color-brand);
    }

    .image-lightbox-zoom-button {
        min-block-size: 2.5rem;
        padding-block: 0.5rem;
        padding-inline: 1rem;
        border: 0;
        border-radius: 0.25rem;
        background: var(--color-bg-default);
        color: var(--color-text-default);
        font-family: var(--font-ui);
        font-size: 1.5rem;
        cursor: pointer;
    }

    .image-lightbox-zoom-button:disabled {
        cursor: not-allowed;
        background-color: var(--color-gs-light-secondary);
    }

    .image-lightbox-zoom-slider-button:disabled {
        cursor: not-allowed;
        color: var(--color-gs-light-secondary);
    }
</style>

<div
    class="image-lightbox"
    role="dialog"
    aria-modal="true"
    aria-label={dialogLabel}
    data-zoom-scale={transformState.scale}
    tabindex="-1"
    bind:this={dialog}
    use:focusTrap
    onkeydown={handleKeyDown}
    onclick={handleOverlayClick}>
    <div class="image-lightbox-controls image-lightbox-close inverted">
        <IconBarButton label={__('Close')} icon="remove-16" dataTestId="image-zoom-close" on:click={close} />
    </div>
    <div class="image-lightbox-controls image-lightbox-zoom-controls">
        <div class="image-lightbox-zoom-slider" data-test-id="image-zoom-slider">
            <button
                class="image-lightbox-zoom-slider-button"
                type="button"
                disabled={isAtMinimumScale}
                aria-label={__('Zoom out')}
                data-test-id="image-zoom-slider-out"
                onclick={() => zoomBy(-zoomStep)}>
                −
            </button>
            <Slider
                min={zoomPercentMinimum}
                max={zoomPercentMaximum}
                step={zoomPercentStep}
                value={zoomPercent}
                ariaLabel={__('Image zoom')}
                on:change={handleZoomSliderChange} />
            <button
                class="image-lightbox-zoom-slider-button"
                type="button"
                disabled={isAtMaximumScale}
                aria-label={__('Zoom in')}
                data-test-id="image-zoom-slider-in"
                onclick={() => zoomBy(zoomStep)}>
                +
            </button>
        </div>
        <div class="image-lightbox-zoom-buttons">
            <button
                class="image-lightbox-zoom-button"
                type="button"
                disabled={isAtMinimumScale}
                data-test-id="image-zoom-out"
                onclick={() => zoomBy(-zoomStep)}>
                {__('Zoom out')}
            </button>
            <button class="image-lightbox-zoom-button" type="button" data-test-id="image-zoom-reset" onclick={reset}>
                {__('100%')}
            </button>
            <button
                class="image-lightbox-zoom-button"
                type="button"
                disabled={isAtMaximumScale}
                data-test-id="image-zoom-in"
                onclick={() => zoomBy(zoomStep)}>
                {__('Zoom in')}
            </button>
        </div>
    </div>
    <div
        class="image-lightbox-viewport"
        role="group"
        aria-label={__('Zoomable image')}
        bind:this={viewport}
        onpointerdown={handlePointerDown}
        onpointermove={handlePointerMove}
        onpointerup={handlePointerUp}
        onpointercancel={handlePointerUp}
        onwheel={handleWheel}>
        <img
            class="image-lightbox-image"
            bind:this={image}
            {src}
            {alt}
            draggable="false"
            style={imageStyle}
            onload={updateBounds} />
    </div>
</div>
